import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Eye, Search, XCircle, Paperclip, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const REJECTED_STATUS_OPTIONS = [
  { value: 'all', label: 'All Rejected' },
  { value: 'MANAGER_REJECTED', label: 'Manager Rejected' },
  { value: 'ADDITIONAL_MANAGER_REJECTED', label: '2nd Manager Rejected' },
  { value: 'INTERNAL_AUDIT_REJECTED', label: 'Audit Rejected' },
  { value: 'ACCOUNTS_REJECTED', label: 'Accounts Rejected' },
  { value: 'FINANCE_HEAD_REJECTED', label: 'Finance Rejected' },
];

export const FinanceHeadRejectedVouchersPage = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchVouchers();
  }, []);

  const fetchVouchers = async () => {
    try {
      const data = await vouchersApi.getAll();
      // Filter only rejected vouchers
      const rejectedVouchers = data.filter(v => 
        v.status === 'MANAGER_REJECTED' ||
        v.status === 'ADDITIONAL_MANAGER_REJECTED' ||
        v.status === 'INTERNAL_AUDIT_REJECTED' ||
        v.status === 'ACCOUNTS_REJECTED' ||
        v.status === 'FINANCE_HEAD_REJECTED'
      );
      setVouchers(rejectedVouchers);
    } catch (error) {
      toast.error('Failed to fetch vouchers');
    } finally {
      setLoading(false);
    }
  };

  const filteredVouchers = vouchers.filter((voucher) => {
    const matchesSearch =
      voucher.voucherNo?.toLowerCase().includes(search.toLowerCase()) ||
      voucher.beneficiaryName?.toLowerCase().includes(search.toLowerCase()) ||
      voucher.department?.toLowerCase().includes(search.toLowerCase()) ||
      voucher.createdByName?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || voucher.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredVouchers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVouchers = filteredVouchers.slice(startIndex, endIndex);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-64 bg-gray-100 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-head-rejected-vouchers-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold tracking-tight">Rejected Vouchers</h1>
        <p className="text-muted-foreground text-sm mt-1">
          View all rejected vouchers across the approval workflow
        </p>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Rejected Vouchers ({filteredVouchers.length})
            </CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search vouchers..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-full sm:w-64"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-56" data-testid="status-filter">
                  <SelectValue placeholder="Filter by rejection type" />
                </SelectTrigger>
                <SelectContent>
                  {REJECTED_STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredVouchers.length === 0 ? (
            <div className="text-center py-12">
              <XCircle className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground">No rejected vouchers</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'No vouchers have been rejected'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Voucher No</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Created By</TableHead>
                      <TableHead className="font-semibold">Beneficiary</TableHead>
                      <TableHead className="font-semibold">Amount</TableHead>
                      <TableHead className="font-semibold">Rejected At</TableHead>
                      <TableHead className="text-right font-semibold">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedVouchers.map((voucher) => (
                      <TableRow
                        key={voucher.id}
                        className="hover:bg-gray-50"
                        data-testid={`voucher-row-${voucher.id}`}
                      >
                        <TableCell className="font-mono font-medium">
                          <div className="flex items-center gap-1">
                            {voucher.voucherNo || (
                              <span className="text-muted-foreground">Draft</span>
                            )}
                            {voucher.attachments && voucher.attachments.length > 0 && (
                              <Paperclip className="w-3.5 h-3.5 text-[#6a5da4]" title={`${voucher.attachments.length} attachment(s)`} />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(voucher.date)}</TableCell>
                        <TableCell>{voucher.createdByName}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{voucher.beneficiaryName}</p>
                            <p className="text-xs text-muted-foreground">{voucher.department}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono">
                          {formatCurrency(voucher.amountTotal, voucher.currency)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={voucher.status} size="sm" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/finance-head/vouchers/${voucher.id}`)}
                            data-testid={`view-voucher-${voucher.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-2">
                  <p className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredVouchers.length)} of {filteredVouchers.length} vouchers
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          if (page === 1 || page === totalPages) return true;
                          if (Math.abs(page - currentPage) <= 1) return true;
                          return false;
                        })
                        .map((page, index, arr) => (
                          <React.Fragment key={page}>
                            {index > 0 && arr[index - 1] !== page - 1 && (
                              <span className="px-2 text-muted-foreground">...</span>
                            )}
                            <Button
                              variant={currentPage === page ? "default" : "outline"}
                              size="sm"
                              className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-[#6a5da4] hover:bg-[#5a4d94]' : ''}`}
                              onClick={() => setCurrentPage(page)}
                              data-testid={`pagination-page-${page}`}
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      data-testid="pagination-next"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FinanceHeadRejectedVouchersPage;
